import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import StarRateIcon from '@mui/icons-material/StarRate';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ImageLogoBox from '../boxes/ImageLogoBox';
import BulletListWithItems from '../lists/BulletListWithItems';
import HIGHLIGHTED_CHIP_CLASS_NAME from '../../theme/constants/chip';

const CessionTypeOption = ({
  src,
  altIcon,
  iconSx,
  recommended,
  label,
  description,
  subItems,
  footer,
  isMobile,
}) => (
  <Box display="flex" flexDirection="row" alignItems="flex-start">
    {!isMobile && (
    <ImageLogoBox
      src={src}
      alt={label}
      IconComponent={altIcon || FactCheckOutlinedIcon}
      sx={{}}
      iconSx={iconSx}
      imgWidth={60}
      imgHeight={35}
    />
    )}
    <Box display="flex" flexDirection="column" ml={{ xs: 0, md: 3 }}>
      <Box
        display="flex"
        sx={{
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column-reverse', md: 'row' },
          gap: { xs: 1, md: 0 },
        }}
      >
        <Typography
          variant={isMobile ? 'small_2' : 'medium_1'}
          fontWeight={600}
          component="span"
        >
          {label}
        </Typography>
        {recommended && (
          <Chip
            icon={<StarRateIcon />}
            label="Recomendado"
            size="small"
            className={HIGHLIGHTED_CHIP_CLASS_NAME}
            sx={{
              py: 0,
              height: 20,
              marginLeft: { xs: 0, md: 1.5 },
              fontSize: { xs: 12, md: 14 },
              '& .MuiChip-icon': {
                fontSize: { xs: 12, md: 15 },
              },
            }}
          />
        )}
      </Box>
      <BulletListWithItems
        title={description}
        titleProps={{
          variant: isMobile ? 'small_1' : 'small_2',
          color: 'gray.A600',
          fontWeight: 500,
        }}
        items={subItems.map(({ id, text, checked }) => ({
          id,
          primary: (
            <Stack direction="row">
              <Typography
                variant={isMobile ? 'small_1' : 'small_2'}
                fontWeight={500}
                color={checked ? 'gray.A400' : 'gray.A600'}
              >
                {text}
              </Typography>
              {checked && (
                <Typography
                  variant={isMobile ? 'small_1' : 'small_2'}
                  color="success.A700"
                  fontWeight={500}
                  ml={0.5}
                >
                  Listo
                </Typography>
              )}
            </Stack>
          ),
          color: checked ? 'gray.A400' : 'gray.A600',
          fontSize: 14,
        }))}
        sx={{ mt: '1px' }}
        disablePadding
        dense
      />
      {footer && (
        <Typography
          variant="caption"
          fontSize={10}
          sx={{ opacity: 0.9, mt: { xs: 1, md: 2 } }}
        >
          {footer}
        </Typography>
      )}
    </Box>
  </Box>
);

CessionTypeOption.propTypes = {
  src: PropTypes.string,
  altIcon: PropTypes.elementType,
  recommended: PropTypes.bool,
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  subItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      checked: PropTypes.bool,
      text: PropTypes.string,
    }),
  ),
  footer: PropTypes.string,
  isMobile: PropTypes.bool,
  iconSx: PropTypes.shape({}),
};

CessionTypeOption.defaultProps = {
  altIcon: null,
  recommended: false,
  src: null,
  footer: null,
  subItems: [],
  isMobile: false,
  iconSx: null,
};

export default React.memo(CessionTypeOption);
